import { motion } from "framer-motion";
import { ExternalLink, TrendingUp, Plane, Users, Wallet, Sparkles } from "lucide-react";
import { Reveal, MaskLines, SectionHead, Counter, EASE } from "@/components/anim";
import { STATS, MARKET_BARS, SOURCES } from "@/lib/content";

const METRICS = [
  { icon: Plane, value: 38575, suffix: "+", label: "Drones registered (UIN)", src: "Digital Sky, Feb 2026" },
  { icon: Users, value: 39890, suffix: "+", label: "Certified remote pilots", src: "PIB / MoCA, Feb 2026" },
  { icon: Users, value: 251, suffix: "", label: "Authorised RPTOs", src: "PIB / MoCA, Feb 2026" },
  { icon: Sparkles, value: 1094, suffix: "+", label: "Drones under Namo Drone Didi", src: "PIB, 2025-26" },
  { icon: Users, value: 25791, suffix: "+", label: "PMKVY 4.0 drone-role candidates", src: "PIB, Dec 2025" },
  { icon: Plane, value: 145, suffix: "+", label: "Indigenous type-certified models", src: "Industry, 2025-26" },
  { icon: Wallet, value: 5, suffix: "%", label: "GST on drones & simulators", src: "GoI, Sept 2025" },
  { icon: TrendingUp, value: 90, suffix: "%", label: "Airspace Green Zone up to 400 ft", src: "Drone Rules, GoI" },
];

const POLICY = [
  {
    title: "Production Linked Incentive (PLI)",
    desc: "Component-level PLI support pushes Indian drone manufacturing up the value chain — the backdrop Aeris builds on.",
  },
  {
    title: "5% uniform GST",
    desc: "Since September 2025, drones and flight simulators attract a uniform 5% GST, easing acquisition and training economics.",
  },
  {
    title: "Green-zone airspace",
    desc: "About 90% of Indian airspace up to 400 ft needs no prior flight permission — missions launch faster.",
  },
  {
    title: "Digital Sky → eGCA",
    desc: "Registration and certification services have migrated from Digital Sky to the eGCA portal, streamlining compliance.",
  },
  {
    title: "Namo Drone Didi",
    desc: "1,094+ drones already delivered to women's self-help groups for precision spraying, with a 15,000-drone programme ambition.",
  },
];

export default function Insights() {
  const max = Math.max(...MARKET_BARS.map((b) => b.value));

  return (
    <main>
      <section className="relative overflow-hidden hud-grid noise pt-36 md:pt-44 pb-16 md:pb-24">
        <div className="pointer-events-none absolute -top-32 right-1/4 h-[420px] w-[420px] rounded-full bg-kinetic/10 blur-[130px]" />
        <div className="relative mx-auto max-w-7xl px-5 md:px-8">
          <MaskLines
            lines={[
              <span key="e" className="font-hud text-[11px] md:text-xs uppercase tracking-[0.3em] text-kinetic">
                [ INSIGHTS ]
              </span>,
            ]}
          />
          <MaskLines
            className="mt-6 font-display font-extrabold tracking-[-0.02em] leading-[0.98] text-[clamp(2.4rem,6vw,5.5rem)]"
            lines={["INDIA'S DRONE", <span key="2"><span className="text-kinetic">DECADE</span> IN DATA.</span>]}
            delay={0.2}
          />
          <MaskLines
            className="mt-7 max-w-2xl text-base md:text-lg text-[#9CA3AF] leading-relaxed"
            lines={[
              <span key="s" className="block">
                Live figures from government releases, market researchers and the DGCA ecosystem —
                the platforms building the same sky Aeris flies in.
              </span>,
            ]}
            delay={0.55}
          />
        </div>
      </section>

      <section className="border-t border-white/10 py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-5 md:px-8">
          <SectionHead sec="MARKET" eyebrow="Size & trajectory" title="A billion-dollar market, tripling by 2030." />
          <div className="mt-14 grid gap-10 lg:grid-cols-[0.9fr_1.1fr] lg:items-end">
            <Reveal>
              <div className="rounded-2xl border border-white/10 bg-panel/50 p-8" data-testid="insights-market-chart">
                <p className="font-hud text-[11px] uppercase tracking-[0.24em] text-kinetic">
                  India drone market value (USD)
                </p>
                <div className="mt-10 flex h-64 items-end gap-8 md:gap-14">
                  {MARKET_BARS.map((b, i) => (
                    <div key={b.year} className="flex flex-1 flex-col items-center gap-3">
                      <p className="font-hud text-xs text-kinetic">{b.label}</p>
                      <motion.div
                        initial={{ height: 0 }}
                        whileInView={{ height: `${(b.value / max) * 100}%` }}
                        viewport={{ once: true, amount: 0.4 }}
                        transition={{ duration: 1.1, delay: i * 0.15, ease: EASE }}
                        className="w-full rounded-t-lg bg-gradient-to-t from-kinetic/25 to-kinetic"
                      />
                      <p className="font-hud text-sm text-[#9CA3AF]">{b.year}</p>
                    </div>
                  ))}
                </div>
                <p className="mt-6 font-hud text-[10px] uppercase tracking-[0.18em] text-[#6B7280]">
                  Source: IMARC Group — India Drones Market
                </p>
              </div>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="space-y-6">
                <div className="rounded-2xl border border-kinetic/25 bg-kinetic/5 p-8 glow-cyan" data-testid="insights-market-size">
                  <Counter
                    value={1.31}
                    prefix="$"
                    className="font-display text-4xl md:text-5xl font-extrabold text-kinetic tracking-tight"
                  />
                  <p className="mt-3 text-[#E5E7EB]">
                    Billion market in 2025, projected to reach USD 1.43 B in 2026 and USD 3.23 B by 2030.
                  </p>
                </div>
                <div className="rounded-2xl border border-white/10 bg-panel/50 p-8" data-testid="insights-hardware-share">
                  <p className="font-display text-lg font-bold">Hardware leads, services accelerate</p>
                  <div className="mt-5 h-3 w-full overflow-hidden rounded-full bg-white/10">
                    <motion.div
                      initial={{ width: 0 }}
                      whileInView={{ width: "54%" }}
                      viewport={{ once: true, amount: 0.6 }}
                      transition={{ duration: 1.2, ease: EASE }}
                      className="h-full rounded-full bg-kinetic"
                    />
                  </div>
                  <div className="mt-3 flex justify-between font-hud text-[11px] tracking-[0.16em] text-[#9CA3AF]">
                    <span className="text-kinetic">HARDWARE — 54% OF MARKET VALUE</span>
                    <span>SOFTWARE &amp; DAAS — FASTEST GROWING</span>
                  </div>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="border-y border-white/10 bg-surface py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-5 md:px-8">
          <SectionHead sec="ECOSYSTEM" eyebrow="National telemetry" title="The numbers behind the network." />
          <div className="mt-14 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {METRICS.map((m, i) => (
              <Reveal key={m.label} delay={i * 0.05} className="h-full">
                <div className="h-full rounded-2xl border border-white/10 bg-panel/50 p-6 transition-all duration-300 hover:border-kinetic/40" data-testid={`insights-metric-${i + 1}`}>
                  <m.icon className="h-5 w-5 text-kinetic" />
                  <p className="mt-6 font-display text-3xl font-extrabold tracking-tight">
                    <Counter value={m.value} suffix={m.suffix} />
                  </p>
                  <p className="mt-2 text-sm text-[#D1D5DB] leading-snug">{m.label}</p>
                  <p className="mt-3 font-hud text-[10px] uppercase tracking-[0.16em] text-[#6B7280]">{m.src}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-5 md:px-8 grid gap-14 lg:grid-cols-2">
          <div>
            <SectionHead sec="POLICY TAILWINDS" eyebrow="The wind at every rotor" title="Government is clearing the runway." />
            <div className="mt-10 space-y-0">
              {POLICY.map((p, i) => (
                <Reveal key={p.title} delay={i * 0.06}>
                  <div className="border-t border-white/10 py-6 last:border-b" data-testid={`insights-policy-${i + 1}`}>
                    <h3 className="font-display text-lg font-bold">{p.title}</h3>
                    <p className="mt-2 text-sm text-[#9CA3AF] leading-relaxed">{p.desc}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
          <div>
            <SectionHead sec="SOURCES" eyebrow="Read the primary data" title="Every figure, traceable." />
            <div className="mt-10 space-y-4">
              {SOURCES.map((s, i) => (
                <Reveal key={i} delay={i * 0.05}>
                  <a
                    href={s.url}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`insights-source-${i + 1}`}
                    className="group flex items-start gap-4 rounded-xl border border-white/10 bg-panel/40 p-5 transition-all duration-300 hover:border-kinetic/50"
                  >
                    <ExternalLink className="mt-1 h-4 w-4 shrink-0 text-kinetic" />
                    <p className="text-sm text-[#D1D5DB] group-hover:text-white transition-colors">{s.name}</p>
                  </a>
                </Reveal>
              ))}
              <Reveal delay={0.3}>
                <p className="font-hud text-[10px] uppercase tracking-[0.18em] text-[#6B7280]" data-testid="insights-asof-note">
                  FIGURES AS OF FEBRUARY 2026 · AERIS INDIA TECHNOLOGY
                </p>
              </Reveal>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
